import { useEffect, useState } from "react";
import { getUserData, authenticatedFetch } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  CreditCard, 
  Download, 
  Eye,
  Trash2,
  Calendar,
  DollarSign,
  FileText,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  Search
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Cliente {
  id: number;
  company_name: string;
  status?: string;
  tickets_open?: number;
  last_ticket?: string;
}

interface Fatura {
  id: number;
  folio_id: string;
  invoice_number: string;
  description: string;
  total: number;
  currency: string;
  issue_date: string;
  due_date: string;
  paid_date: string | null;
  status: string;
  payment_method: string | null;
  invoice_file_url: string;
  notes: string | null;
  company_name: string;
  trading_name: string;
}

const statusConfig: Record<string, { label: string; color: string }> = {
  draft: { label: "Rascunho", color: "bg-gray-500/20 text-gray-700 dark:text-gray-400" },
  sent: { label: "Enviada", color: "bg-blue-500/20 text-blue-700 dark:text-blue-400" },
  overdue: { label: "Vencida", color: "bg-orange-500/20 text-orange-700 dark:text-orange-400" },
  paid: { label: "Paga", color: "bg-green-500/20 text-green-700 dark:text-green-400" },
  cancelled: { label: "Cancelada", color: "bg-red-500/20 text-red-700 dark:text-red-400" },
};

const AdminFaturas = () => {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [clienteSelecionado, setClienteSelecionado] = useState<string>("");
  const [faturas, setFaturas] = useState<Fatura[]>([]);
  const [isLoadingClientes, setIsLoadingClientes] = useState(true);
  const [isLoadingFaturas, setIsLoadingFaturas] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const user = getUserData();

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async () => {
    setIsLoadingClientes(true);
    setError(null);
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
      const response = await authenticatedFetch(`${API_BASE_URL}/clientes/lista.php`);
      
      if (!response.ok) {
        throw new Error('Erro ao carregar clientes');
      }
      
      const data = await response.json();
      setClientes(data.data || []);
    } catch (err: any) {
      console.error('Erro ao carregar clientes:', err);
      setError(err.message || 'Erro ao carregar clientes');
    } finally {
      setIsLoadingClientes(false);
    }
  };

  const loadFaturas = async (clientId: string) => {
    if (!clientId) {
      setFaturas([]);
      return;
    }

    setIsLoadingFaturas(true);
    setError(null);
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
      const response = await authenticatedFetch(`${API_BASE_URL}/faturas?client_id=${clientId}`);
      
      if (!response.ok) {
        throw new Error('Erro ao carregar faturas');
      }
      
      const data = await response.json();
      setFaturas(data.data || []);
    } catch (err: any) {
      console.error('Erro ao carregar faturas:', err);
      setError(err.message || 'Erro ao carregar faturas');
    } finally {
      setIsLoadingFaturas(false);
    }
  };

  const handleClienteChange = (clientId: string) => {
    setClienteSelecionado(clientId);
    if (clientId) {
      loadFaturas(clientId);
    } else {
      setFaturas([]);
    }
  };

  const handleDelete = async (fatura: Fatura) => {
    if (!confirm(`Deseja realmente excluir a fatura #${fatura.invoice_number}?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
      const response = await authenticatedFetch(
        `${API_BASE_URL}/faturas?id=${fatura.id}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao excluir fatura');
      }

      setMessage({ type: "success", text: `Fatura #${fatura.invoice_number} excluída com sucesso!` });
      loadFaturas(clienteSelecionado);
    } catch (err: any) {
      console.error('Erro ao excluir fatura:', err);
      setMessage({ type: "error", text: err.message || 'Erro ao excluir fatura' });
    }
  };

  const formatCurrency = (value: number, currency: string = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const handleDownload = async (fatura: Fatura) => {
    if (!fatura.invoice_file_url) return;
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
      const filePath = fatura.invoice_file_url.replace('/uploads/', '');
      const fileUrl = `${API_BASE_URL}/uploads/serve.php?file=${encodeURIComponent(filePath)}`;
      
      const response = await authenticatedFetch(fileUrl);
      
      if (!response.ok) {
        throw new Error('Erro ao baixar arquivo');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fatura_${fatura.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      setMessage({ type: "error", text: 'Erro ao baixar arquivo. Tente novamente.' });
    }
  };

  const handleView = async (fatura: Fatura) => {
    if (!fatura.invoice_file_url) return;
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
      const filePath = fatura.invoice_file_url.replace('/uploads/', '');
      const fileUrl = `${API_BASE_URL}/uploads/serve.php?file=${encodeURIComponent(filePath)}`;
      
      const response = await authenticatedFetch(fileUrl);
      
      if (!response.ok) {
        throw new Error('Erro ao carregar arquivo');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Erro ao visualizar arquivo:', error);
      setMessage({ type: "error", text: 'Erro ao visualizar arquivo. Tente novamente.' });
    }
  };

  if (isLoadingClientes) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Carregando clientes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
                <FileText className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Erro ao carregar faturas</h3>
                <p className="text-sm text-muted-foreground mt-2">{error}</p>
              </div>
              <Button onClick={loadClientes} variant="outline">
                Tentar novamente
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === "success" 
              ? "bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20" 
              : "bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20"
          }`}>
            {message.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            {message.text}
            <button onClick={() => setMessage(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Administração de Faturas</h1>
              <p className="text-muted-foreground">Gerencie faturas publicadas no portal</p>
            </div>
          </div>
        </div>

        {/* Filtro de Cliente */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Selecione um Cliente
                </label>
                <select
                  value={clienteSelecionado}
                  onChange={(e) => handleClienteChange(e.target.value)}
                  className="w-full px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">-- Selecione um cliente --</option>
                  {clientes.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.company_name}
                    </option>
                  ))}
                </select>
              </div>
              {clienteSelecionado && (
                <Button
                  onClick={() => loadFaturas(clienteSelecionado)}
                  variant="outline"
                  className="mt-6"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Atualizar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Loading Faturas */}
        {isLoadingFaturas && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground">Carregando faturas...</p>
            </div>
          </div>
        )}

        {/* Lista de Faturas */}
        {!isLoadingFaturas && !clienteSelecionado && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4 py-8">
                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto">
                  <Search className="h-8 w-8 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Selecione um Cliente</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Escolha um cliente no filtro acima para visualizar suas faturas.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!isLoadingFaturas && clienteSelecionado && faturas.length === 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4 py-8">
                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto">
                  <CreditCard className="h-8 w-8 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Nenhuma fatura encontrada</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Este cliente não possui faturas publicadas no portal.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!isLoadingFaturas && clienteSelecionado && faturas.length > 0 && (
          <div className="grid gap-4">
            {faturas.map((fatura) => {
              const statusInfo = statusConfig[fatura.status] || statusConfig.draft;
              
              return (
                <Card key={fatura.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      {/* Info Principal */}
                      <div className="flex-1 space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-3">
                              <h3 className="text-lg font-semibold">
                                Fatura #{fatura.invoice_number}
                              </h3>
                              <Badge className={cn("text-xs", statusInfo.color)}>
                                {statusInfo.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {fatura.company_name || fatura.trading_name}
                            </p>
                            {fatura.description && (
                              <p className="text-sm text-muted-foreground">
                                {fatura.description}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground">Emissão</p>
                              <p className="font-medium">{formatDate(fatura.issue_date)}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground">Vencimento</p>
                              <p className="font-medium">{formatDate(fatura.due_date)}</p>
                            </div>
                          </div>

                          {fatura.paid_date && (
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-green-500" />
                              <div>
                                <p className="text-xs text-muted-foreground">Pagamento</p>
                                <p className="font-medium text-green-600">{formatDate(fatura.paid_date)}</p>
                              </div>
                            </div>
                          )}

                          <div className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-xs text-muted-foreground">Valor</p>
                              <p className="font-bold text-lg">{formatCurrency(fatura.total, fatura.currency)}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Ações */}
                      <div className="flex gap-2">
                        {fatura.invoice_file_url && (
                          <>
                            <Button
                              onClick={() => handleView(fatura)}
                              variant="outline"
                              size="sm"
                              className="gap-2"
                            >
                              <Eye className="h-4 w-4" />
                              Ver
                            </Button>
                            <Button
                              onClick={() => handleDownload(fatura)}
                              variant="outline"
                              size="sm"
                              className="gap-2"
                            >
                              <Download className="h-4 w-4" />
                              PDF
                            </Button>
                          </>
                        )}
                        <Button
                          onClick={() => handleDelete(fatura)}
                          variant="outline"
                          size="sm"
                          className="gap-2 text-red-600 dark:text-red-400 hover:bg-red-500/10 border-red-500/30"
                        >
                          <Trash2 className="h-4 w-4" />
                          Excluir
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFaturas;
